import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import jwt_decode from 'jwt-decode';

import { Preferences } from '@capacitor/preferences';
import { AuthService } from './auth.service'; // Replace with the actual path to your authentication service

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshingAccessToken: boolean = false;
  private accessTokenRefreshSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private authService: AuthService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const excludedUrls = ['login', 'signup', 'refresh']; // Add the login and signup URLs here

    // Check if the request URL matches any excluded URL
    if (excludedUrls.some(url => request.url.includes(url))) {
      return next.handle(request); // Skip the interceptor for excluded URLs
    }

    return from(this.authService.getAccessToken()).pipe(
      switchMap(accessToken => {

        if (accessToken) {

          // Check if the access token has expired
          if (this.isTokenExpired(accessToken)) {
            if (!this.refreshingAccessToken) {
              this.refreshingAccessToken = true;
              this.accessTokenRefreshSubject.next(null); // Start the refresh token request

              return this.authService.refreshAccessToken().pipe(
                switchMap(newAccessToken => {
                  this.accessTokenRefreshSubject.next(newAccessToken); // Signal successful refresh

                  // Reset the lock to allow future refreshes
                  this.refreshingAccessToken = false;
                  
                  return next.handle(this.addAuthorizationHeader(request, newAccessToken));
                }),
                catchError(error => {
                  this.accessTokenRefreshSubject.next(null); // Signal refresh failure
                  
                  // Reset the lock to allow future refreshes
                  this.refreshingAccessToken = false;

                  // Handle any error during token refresh
                  // For example, you can redirect to the login page, logout the user, or perform any other necessary actions
                  return throwError(error);
                }),
                finalize(() => {
                  this.refreshingAccessToken = false;
                })
              );
            } else {
              console.log('Token: New Request waiting on a token...');
              return this.accessTokenRefreshSubject.pipe(
                filter(token => token !== null), // Wait until a non-null token is emitted
                take(1),
                switchMap(newAccessToken => {
                  return next.handle(this.addAuthorizationHeader(request, newAccessToken));
                })
              );
            }
          } else {
            // Clone the request and add the authorization header
            const authenticatedRequest = this.addAuthorizationHeader(request, accessToken);
            return next.handle(authenticatedRequest);
          }
        } else {
          // No access token available, proceed with the original request
          return next.handle(request);
        }
      })
    ) as Observable<HttpEvent<any>>;
  }

  private addAuthorizationHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private isTokenExpired(token: string): boolean {
    try {
      const decodedToken: any = jwt_decode(token);
      const expirationDate = new Date(decodedToken.exp * 1000); // Convert expiration time to milliseconds

      // Compare the expiration date with the current time
      return expirationDate < new Date();
    } catch (error) {
      // Error occurred while decoding the token
      return true; // Assume token is expired to be safe
    }
  }
}
